import { zodResolver } from '@hookform/resolvers/zod';
import React, { useContext, useRef, useState } from 'react';

import { useForm } from 'react-hook-form';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError } from '_lib/api/apiErrors';
import {
  AccountUserCreateDTO,
  AccountUserCreateSchema,
  AccountUserDetailDTO,
  RoleEnum,
  UserDetailDTO
} from '_lib/api/apiTypes.shim';

import { WarningAlert } from '_lib/components/Alert/Alert';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Button } from '_lib/components/Button/Button';
import { Divider } from '_lib/components/Divider/Divider';
import { ErrorDetailsAccordion } from '_lib/components/ErrorDetailsAccordion/ErrorDetailsAccordion';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';

import { Stack } from '_lib/components/Stack/Stack';
import { Typography } from '_lib/components/Typography/Typography';
import { useIsOverflowing } from '_lib/hooks/useIsOverflowing';

import { extractAllErrors } from '_lib/utils/helpers';
import { apiSubmitHandler } from '_lib/utils/hookFormUtils';

import { logDebug } from '_lib/utils/logging';
import { useOurSnackbar } from '_lib/utils/snackUtils';

import { AuthUser } from '../auth/auth.types';

import { AccountUserForm } from './AccountUserForm';

type AccountUserCreateProps = {
  accountId: string | undefined;
  callCenterId: string | undefined;
  authUser: AuthUser | undefined;
  onSave?: (accountUser: AccountUserDetailDTO) => void;
  isFetchingFromProps?: boolean;
};

export const AccountUserCreate = (props: AccountUserCreateProps) => {
  const { accountId, callCenterId, isFetchingFromProps, onSave, authUser } = props;

  const [nonFieldErrors, setNonFieldErrors] = useState<string[]>();
  const [allErrors, setAllErrors] = useState<string[]>();

  const { enqueueSnackbar } = useOurSnackbar();

  const scrollTriggerTarget = useRef<HTMLDivElement>(null);
  const { isOverflowing } = useIsOverflowing(scrollTriggerTarget);

  const api = useContext(ApiContext);

  const accountUserPostMutation = api.accountUsers.useCreate();

  const accountUserFormContext = useForm<AccountUserCreateDTO>({
    context: {
      authUser,
      callCenterId,
      accountId
    },
    resolver: (
      values,
      context: { authUser?: UserDetailDTO; callCenterId?: string; accountId?: string } | undefined,
      options
    ) => {
      const resolver = zodResolver(AccountUserCreateSchema);
      return resolver(
        {
          ...values,
          accountId: context?.accountId || context?.authUser?.accountId || values.accountId,
          callCenterId: context?.callCenterId || values.callCenterId
        },
        context,
        options
      );
    },
    defaultValues: {
      role: RoleEnum.USER
    },
    mode: 'onChange',
    shouldFocusError: true
  });

  const handleSave = apiSubmitHandler(
    accountUserFormContext.handleSubmit,
    accountUserFormContext.setError,
    async (data, { setFormErrors }) => {
      setNonFieldErrors(undefined);
      setAllErrors(undefined);
      try {
        const accountUserDetail = await accountUserPostMutation.create({ data, addRecordToCache: true });
        enqueueSnackbar('User created');
        onSave?.(accountUserDetail);
      } catch (err) {
        if (err instanceof BadRequestError) {
          setNonFieldErrors(err.responseData?.nonFieldErrors);
          setFormErrors(err);
        }
      }
    },
    {
      onInvalid: (errors) => {
        enqueueSnackbar('Validation Error occurred. Please check inputs and try again', { variant: 'error' });
        const allErrors = extractAllErrors(errors);
        setAllErrors(allErrors);
        logDebug(errors, allErrors);
      }
    }
  );

  return (
    <FlexBox
      sx={(theme) => ({
        backgroundColor: theme.palette.background.default,
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        position: 'relative'
      })}>
      <BackdropSpinner open={accountUserPostMutation.isPending || !!isFetchingFromProps} />
      {!isFetchingFromProps && accountId === undefined && (
        <WarningAlert sx={{ justifyContent: 'center' }}>
          <Typography>Select an Account to create an Account User</Typography>
        </WarningAlert>
      )}
      {authUser && (
        <>
          <Stack padding={2} useFlexGap spacing={2} overflow='auto' ref={scrollTriggerTarget}>
            {!!nonFieldErrors?.length && <ErrorDisplay errors={nonFieldErrors} sx={{ borderRadius: 1 }} />}
            {!!allErrors?.length && <ErrorDetailsAccordion errors={allErrors} />}
            <AccountUserForm accountUserFormContext={accountUserFormContext} authUser={authUser} />
          </Stack>
          {isOverflowing && <Divider />}
          <FlexBox sx={{ paddingX: 2, paddingY: 1, marginLeft: 'auto' }}>
            <Button
              onClick={handleSave}
              disabled={accountUserPostMutation.isPending}
              text='Save'
              variant='contained'
              size='small'
              sx={{ textTransform: 'none' }}
            />
          </FlexBox>
        </>
      )}
    </FlexBox>
  );
};
